import { LanguageCode, ProductTypeEnum } from '__generated__/globalTypes';
import { useCallback, useMemo, useState } from 'react';
import { PATH } from 'routes/constant';
import { PRODUCT_TYPE, TRACK_EVENT, trackEvent } from 'utils/analytics';
import utils from 'utils/utils';
import { useStyles } from './styles';
import { useQuery } from '@apollo/client';
import { getStyle_getStyle_data } from '__generated__/getStyle';
import { GET_STYLE_BY_ID } from 'graph_ql/styles/queries';
import { AlgoliaResponse, AlgoliaInput } from 'graph_ql/types/types';
import WidgetPopup from 'component/popup/customize_popup';
import config from 'config';
import get from 'lodash/get';
import { WidgetCode } from 'component/popup/constant';
import { createConversation_conversation_messages_elements } from '__generated__/createConversation';
import { getConversation_conversations_data_messages } from '__generated__/getConversation';
import { ReactComponent as OpenNewTab } from 'assets/svg/open-new-tab-black.svg';
import { StyleResponse } from 'containers/session_details/type';
import { GET_ITEM_BY_ID } from 'graph_ql/item/queries';
import { getItem_getItem_data } from '__generated__/getItem';
import { pick } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';
import langUtils, { STRING_CONST } from '../language/utils';
import TypeWriterCustom from '../../component/TypeWriterCustom';
import constant from 'utils/constant';
import useExpandedMessageTaffiIds from './hooks/useExpandedMessageTaffiIds';
import useReceivedAssistantMessage from './hooks/useReceivedAssistantMessage';

interface IShopNowProps {
    lastMessageIndex: number;
    lastElementIndex: number;
    element: createConversation_conversation_messages_elements;
    item: getConversation_conversations_data_messages;
    index: number;
    elementIndex: number;
    handleAnalytics: (
        event: string,
        item: getConversation_conversations_data_messages,
        element: createConversation_conversation_messages_elements,
        child?: createConversation_conversation_messages_elements,
    ) => void;
}
const ShopNow: React.FC<IShopNowProps> = (props) => {
    const classes = useStyles();
    const location = useLocation();
    const { lastMessageIndex, lastElementIndex, element, item, index, elementIndex, handleAnalytics } = props;
    const { userName: customerUserName } = useParams<{ userName: string }>();
    const isStylistView = Boolean(customerUserName);
    const { isExpandedMessage } = useExpandedMessageTaffiIds();
    const { isReceivedAssistantMessage } = useReceivedAssistantMessage();
    const initialStyleItems =
        element.productType === ProductTypeEnum.style
            ? element?.items?.map((item: any) => {
                // find the image from imageJson
                const itemJson = element?.imageJson?.find((json: any) => json.itemId === item.id);
                return {
                    data: {
                        ...item,
                        images: itemJson ? [itemJson.imageCanvas] : item.images,
                    },
                };
            }) ?? []
            : [];
    const [listItem, setListItem] = useState<any[]>(initialStyleItems);
    const [showPopup, setShowPopup] = useState(false);
    const { data: style } = useQuery<AlgoliaResponse<getStyle_getStyle_data, 'algolia_get_style_by_id'>, AlgoliaInput>(
        GET_STYLE_BY_ID,
        {
            variables: {
                filters: `taffiId:${element.productTaffiId}`,
            },
            skip: !element || !element.productTaffiId || element.productType !== ProductTypeEnum.style,
            onCompleted: (data) => {
                const items = get(data, ['algolia_get_style_by_id', 'hits', 0, 'items'], []);
                if (items?.length) {
                    setListItem(
                        items.map((item: any, index: number) => {
                            // find the image from imageJson
                            const itemJson = element?.imageJson?.find((json: any) => json.itemId === item.id);
                            const data = {
                                ...item.data,
                                images: itemJson ? [itemJson.imageCanvas] : item.images,
                            };
                            return {
                                ...item,
                                data,
                            };
                        }),
                    );
                }
            },
        },
    );
    const { data: itemAlgolia } = useQuery<
        AlgoliaResponse<getItem_getItem_data, 'algolia_get_item_by_id'>,
        AlgoliaInput
    >(GET_ITEM_BY_ID, {
        variables: {
            filters: `taffiId:${element.productTaffiId}`,
        },
        skip: !element || !element.productTaffiId || element.productType !== ProductTypeEnum.item,
    });
    const itemData = useMemo(() => get(itemAlgolia, ['algolia_get_item_by_id', 'hits', 0], null), [itemAlgolia]);
    const styleData = useMemo(
        () => get(style, ['algolia_get_style_by_id', 'hits', 0], null) || (null as unknown),
        [style],
    );
    const handleShopClick = useCallback(async () => {
        handleAnalytics(TRACK_EVENT.STYLE_CHAT_PRODUCT_CLICKED, item, element);
        if (element.productType === ProductTypeEnum.item && !isStylistView) {
            trackEvent(TRACK_EVENT.SHOP_THE_PRODUCT, {
                product_type: PRODUCT_TYPE.ITEM,
                contextType: '',
                contextId: '',
                contextTaffiId: '',
                recommendationSource: location.pathname,
                product: itemData
                    ? pick(itemData, ['id', 'taffiId', 'title', 'price', 'currency', 'url'])
                    : {
                        id: element.productId,
                        taffiId: element.productTaffiId,
                        url: element.url,
                    },
            });
        }
        if (element.productType === ProductTypeEnum.item || isStylistView) {
            utils.openUrl(
                element.productType === ProductTypeEnum.item && isStylistView
                    ? `${process.env.REACT_APP_APP_ORIGIN}/item/${element.productTaffiId}`
                    : (element.url as string),
                {
                    from: isStylistView ? `${PATH.STYLE_ASSISTANT}/${customerUserName}` : PATH.STYLE_ASSISTANT,
                    messageTaffiId: item.taffiId,
                    productTaffiId: element.productTaffiId,
                },
            );
        } else {
            setShowPopup(true);
        }
    }, [handleAnalytics, item, element, isStylistView, itemData, customerUserName, location.pathname]);

    const content = useMemo(
        () =>
            !isStylistView
                ? langUtils.getLanguageStr(STRING_CONST.SHOP_NOW, item?.language)
                : element.productType === ProductTypeEnum.item
                    ? langUtils.getLanguageStr(STRING_CONST.VIEW_ITEM, item?.language)
                    : langUtils.getLanguageStr(STRING_CONST.VIEW_STYLE, item?.language),
        [element.productType, isStylistView, item?.language],
    );

    return (
        <>
            <WidgetPopup
                isOpen={showPopup}
                titlePopup={langUtils.getLanguageStr(STRING_CONST.STYLE_DETAILS, item?.language)}
                widgetCode={WidgetCode.ViewDetailStyle}
                listItem={listItem}
                setListItem={() => {}}
                onClose={() => setShowPopup(false)}
                isButton={utils.isAppInWidgetMode() || config.removeCheckOut ? false : true}
                isButtonWhite={false}
                titleButton={'Add all to bag'}
                shortPopup={false}
                handleClick={(e) => {
                    handleAnalytics(TRACK_EVENT.STYLE_CHAT_PRODUCT_CLICKED, item, element, {
                        url: e.product.url,
                        productId: e.product.id,
                        productType: ProductTypeEnum.item,
                        productTaffiId: e.product.taffiId,
                    } as createConversation_conversation_messages_elements);
                }}
                loading={false}
                styleData={styleData ? (styleData as StyleResponse) : undefined}
                language={item?.language || LanguageCode.english}
                pageCode={constant.PAGE_CODE.CHAT}
            />
            <div style={{ padding: '8px 2px' }}>
                <div
                    className={classes.shopNowLabel}
                    onClick={handleShopClick}
                    style={{ ...langUtils.getLanguageStyle(item?.language) }}
                >
                    {index === lastMessageIndex &&
                    elementIndex === lastElementIndex &&
                    isReceivedAssistantMessage &&
                    !isExpandedMessage(item) ? (
                            <TypeWriterCustom content={content} language={item?.language} />
                        ) : (
                            <>{content}</>
                        )}

                    {element.productType === ProductTypeEnum.item && (
                        <span style={{ marginLeft: 5, marginBottom: 2 }}>
                            <OpenNewTab />
                        </span>
                    )}
                </div>
            </div>
        </>
    );
};
export default ShopNow;
